/**
 * RfidScannerPanel.
 * (2026-04-17, Jason Cox) Reusable compact panel for R6 UHF hand scanner.
 * Used by ManualCheckout, TagItems, and ManageTags -- anywhere the user
 * might want to scan RFID tags via BLE instead of typing them.
 *
 * This is *not* a modal -- BLE is a "connect once, scan many" flow, so the
 * panel stays visible while the user works.
 */
import React from 'react';
import { BoltIcon } from '@heroicons/react/24/outline';
import { useRfidScanner } from '../hooks/useRfidScanner';

export interface RfidScannerPanelProps {
  /** Called on every scanned EPC. Trim/dedupe in the consumer. */
  onTagScanned: (epc: string) => void;
  /** Optional title for the panel; defaults to "Hand Scanner". */
  title?: string;
  /** Optional error-surfacing callback (e.g. toast / setMessage). */
  onError?: (message: string) => void;
  /** Initial scan power in dBm. Defaults to 24. */
  initialPower?: number;
  /** Hide the power slider if the caller doesn't want to expose it. */
  hidePowerSlider?: boolean;
  /** Extra className on the outer panel. */
  className?: string;
}

const RfidScannerPanel: React.FC<RfidScannerPanelProps> = ({
  onTagScanned,
  title = 'Hand Scanner',
  onError,
  initialPower = 24,
  hidePowerSlider = false,
  className = '',
}) => {
  const {
    connected,
    connecting,
    deviceInfo,
    statusText,
    batteryLevel,
    scanPower,
    setScanPower,
    connect,
    disconnect,
    trigger,
  } = useRfidScanner({ onTagScanned, initialPower, onError });

  return (
    <div className={`card ${className}`}>
      <div className="card-body">
        <h3 className="text-sm font-semibold text-secondary-900 mb-2">{title}</h3>
        {/* (2026-04-24 / 2026-04-30) The primary CTA stacks icon-over-text
            so this button always renders as a two-line block. The paired
            Camera card uses the same shape, which keeps the two cards'
            buttons identical in height and baseline regardless of which
            label state we're in (Connect Scanner / Scanning... /
            Connected). When connected, the secondary actions (Trigger
            Scan, Disconnect, device info) appear in a wrapping row below
            so the pre- and post-connect states both read cleanly. */}
        <button
          type="button"
          onClick={connect}
          disabled={connected || connecting}
          className="btn-primary w-full flex flex-col items-center justify-center whitespace-nowrap disabled:opacity-50"
        >
          <BoltIcon className="h-5 w-5 mb-1" />
          {/* (2026-04-30) Two stacked text rows so the button always has
              icon + 2 lines of label = 3 visual rows. Across states
              (Connect Scanner / Scanning... / Connected) the second row
              uses a non-breaking space when the label is one word, so the
              height stays identical and the button geometry never shifts. */}
          <span>{connected ? 'Connected' : connecting ? 'Scanning' : 'Connect'}</span>
          <span>{connected ? ' ' : connecting ? '...' : 'Scanner'}</span>
        </button>
        {connected && (
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <button type="button" onClick={trigger} className="btn-primary">
              <BoltIcon className="h-4 w-4 inline mr-1" />
              Trigger Scan
            </button>
            <button type="button" onClick={disconnect} className="btn-secondary">
              Disconnect
            </button>
            <span className="text-sm text-green-700 font-medium ml-2">
              {deviceInfo?.name}
              {batteryLevel >= 0 && ` · Battery: ${batteryLevel}%`}
            </span>
          </div>
        )}
        {connected && !hidePowerSlider && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span>Scan Power:</span>
            <input
              type="range"
              min={1}
              max={30}
              value={scanPower}
              onChange={(e) => setScanPower(parseInt(e.target.value, 10))}
              className="w-48"
            />
            <span>{scanPower} dBm</span>
          </div>
        )}
        <p className="mt-2 text-xs text-secondary-500">{statusText}</p>
      </div>
    </div>
  );
};

export default RfidScannerPanel;
